import { createContext, useContext, useEffect, useState } from 'react';
import { useParams } from 'next/navigation';
import { NodeProps, useReactFlow } from 'reactflow';
import { toast } from 'react-toastify';
import { flatten, omit } from 'lodash';

import { API_ROOT, URL_ROOT } from '#/config/env';
import { createTriggerNode, createActionNode } from '#/services/workspaces';
import updateWorkspaceNode from '#/services/workspaces/nodes/updateWorkspaceNode';
import {
  ActionNodeData,
  INode,
  INodeData,
  IWorkspace,
  TriggerNodeData,
} from '#/types/workspace';

import { TurboNodeData } from '~/Flow/Nodes/TurboNode';

import { useUserContext } from './UserContext';

async function updateNodeData({
  userAddress,
  workspaceUuid,
  nodeProps,
  nodeUuid,
  oldNode,
  oldNodeData,
  updatedNodeData,
}: {
  userAddress: string;
  workspaceUuid: string;
  nodeProps: NodeProps<TurboNodeData & (ActionNodeData | TriggerNodeData)>;
  nodeUuid: string;
  oldNode: any;
  oldNodeData: TurboNodeData & (ActionNodeData | TriggerNodeData);
  updatedNodeData: Partial<INodeData>;
}): Promise<INode> {
  // TODO: schema validation
  // ...

  console.debug(
    '[TurboNode] updateNodeData({ updatedNodeData:',
    JSON.stringify(
      {
        userAddress,
        workspaceUuid,
        nodeProps,
        nodeUuid,
        oldNode,
        oldNodeData,
        updatedNodeData,
      },
      null,
      2,
    ),
    '});',
  );

  console.debug('oldNodeData:', JSON.stringify(oldNodeData));

  const newNodeDataType = updatedNodeData?.type ?? oldNodeData?.type;
  const isChangedNodeDataType = newNodeDataType !== oldNodeData?.type;

  let newNode;
  let newNodeData;

  if (newNodeDataType === 'trigger') {
    newNode = createTriggerNode({
      ...omit(nodeProps, 'data'),
      id: nodeProps.id,
      position: { x: nodeProps.xPos, y: nodeProps.yPos },
      type: 'turbo',
      data: { uuid: nodeUuid, triggerType: 'cron', ...updatedNodeData },
    });
    newNodeData = newNode.data;
  } else {
    newNode = await createActionNode({
      ...omit(nodeProps, 'data'),
      id: nodeProps.id,
      position: { x: nodeProps.xPos, y: nodeProps.yPos },
      type: 'turbo',
      data: {
        ...(isChangedNodeDataType ? {} : oldNodeData),
        ...updatedNodeData,
      },
    });
    newNodeData = newNode.data;
  }

  console.debug('[TurboNode] newNode:', JSON.stringify(newNode));
  console.debug('[TurboNode] newNodeData:', JSON.stringify(newNodeData));

  // Write the node update back to S3
  const updateWorkspaceNodeResult = await updateWorkspaceNode({
    ownerAddress: userAddress!,
    workspaceUuid,
    updatedWorkspaceNode: newNode,
  });
  console.debug(
    '[TurboNode] updateWorkspaceNodeResult:',
    updateWorkspaceNodeResult,
  );

  return newNode;
}

async function isAuthedWithProvider(provider) {
  try {
    const { user } = await (await fetch(`${API_ROOT}/me`)).json();
    return (user.connectedProviders ?? []).includes(provider);
  } catch (error: any) {
    console.error(error);
    return false;
  }
}

async function isMetPrerequisite(node, prerequisite) {
  return prerequisite === 'OAUTH_1_0A'
    ? (node?.data?.serviceKey ?? '').toLowerCase() === 'twitter-v2' &&
        (await isAuthedWithProvider('twitter'))
    : true;
}

async function runPreFlightCheck(workspace) {
  const nodePrerequisites = await Promise.all(
    flatten(
      workspace.nodes.map((node) =>
        (node?.data?.prerequisites ?? []).map(async (prerequisite) => ({
          nodeLabel: `${node?.data?.title} ${node?.data?.subline}`,
          prerequisite,
          isMet: await isMetPrerequisite(node, prerequisite),
          message:
            prerequisite === 'OAUTH_1_0A'
              ? 'Authentication required.'
              : prerequisite,
          messageDetails: `Login from \`${URL_ROOT}/dashboard/account/connected-accounts\` to continue.`,
        })),
      ),
    ),
  );
  const unmetPrerequisites = nodePrerequisites.filter((p) => !p.isMet);
  return {
    isReadyToRun: unmetPrerequisites.length === 0,
    unmetPrerequisites,
  };
}

type UpdateFunction<T> = (value: T) => void;
interface FlowContextData {
  isLoading: boolean;

  workspace?: IWorkspace;

  canDeleteNode?: boolean;
  setCanDeleteNode: UpdateFunction<boolean>;

  isSettingsShown?: boolean;
  setIsSettingsShown: UpdateFunction<boolean>;
  toggleIsSettingsShown: () => void;

  selectedNodeProps?: any;
  setSelectedNodeProps: UpdateFunction<any>;

  selectedNodeData?: any;
  setSelectedNodeData: UpdateFunction<any>;

  updateWorkflowNodeData: (
    updatedNodeData: Partial<INodeData>,
  ) => Promise<void>;

  isNodeDetailsShown?: boolean;
  setIsNodeDetailsShown: UpdateFunction<boolean>;
  toggleIsNodeDetailsShown: () => void;

  unmetPrerequisites: any[];
  handleRunPreFlightCheck: () => Promise<{
    isReadyToRun: boolean;
    unmetPrerequisites: any[];
  }>;

  isAuthModalShown?: boolean;
  setIsAuthModalShown: UpdateFunction<boolean>;
  toggleIsAuthModalShown: () => void;
}

// Create a new context
export const FlowContext = createContext({
  isLoading: true,

  // workspace: null,

  canDeleteNode: false,
  setCanDeleteNode: (value: boolean) => {},

  isSettingsShown: false,
  setIsSettingsShown: (value: boolean) => {},
  toggleIsSettingsShown: () => {},

  selectedNodeProps: null,
  setSelectedNodeProps: (value: any) => {},

  selectedNodeData: null,
  setSelectedNodeData: (value: any) => {},

  updateWorkflowNodeData: async (updatedNodeData: Partial<INodeData>) => {},

  isNodeDetailsShown: false,
  setIsNodeDetailsShown: (value: boolean) => {},
  toggleIsNodeDetailsShown: () => {},

  unmetPrerequisites: [],
  handleRunPreFlightCheck: async () => ({
    isReadyToRun: false,
    unmetPrerequisites: [],
  }),

  isAuthModalShown: false,
  setIsAuthModalShown: (value: boolean) => {},
  toggleIsAuthModalShown: () => {},
} as FlowContextData);

// Custom hook to use the context
export const useFlowContext = () => {
  return useContext(FlowContext);
};

// Provider component
export const FlowProvider = ({ children }) => {
  const { workspace: workspaceUuid } = useParams();

  const [isLoading, setIsLoading] = useState(true);

  const { userSession, user } = useUserContext();
  const userAddress = userSession?.address;

  const [workspace, setWorkspace] = useState<IWorkspace>();

  async function fetchWorkspace() {
    console.debug('[fetchWorkspace]', { userAddress, user });
    if (userAddress == null || user == null) {
      return;
    }
    try {
      setIsLoading(true);
      // TODO: check workspace integrity and throw an error inside the system
      // read function
      const currentWorkspace: IWorkspace = await user.getWorkspace(
        workspaceUuid,
      );
      console.debug('[fetchWorkspace] currentWorkspace:', currentWorkspace);
      setWorkspace(currentWorkspace);
    } catch (error) {
      console.error(error);
      // Handle the error as appropriate
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchWorkspace();
  }, [userAddress, user, workspaceUuid]);

  const { getNodes, setNodes } = useReactFlow();

  const [canDeleteNode, setCanDeleteNode] = useState<boolean>(true);

  const [isSettingsShown, setIsSettingsShown] = useState(false);
  function toggleIsSettingsShown() {
    setIsSettingsShown(!isSettingsShown);
  }

  const [selectedNodeProps, setSelectedNodeProps] = useState<any>();
  const [selectedNodeData, setSelectedNodeData] = useState<any>();

  const [isNodeDetailsShown, setIsNodeDetailsShown] = useState<boolean>(false);
  function toggleIsNodeDetailsShown() {
    setIsNodeDetailsShown(!isNodeDetailsShown);
  }

  async function updateWorkflowNodeData(updatedNodeData: Partial<INodeData>) {
    try {
      const nodeUuid = selectedNodeData?.uuid;
      const nodes = getNodes();
      const oldNode = nodes.find((n) => n?.data?.uuid === nodeUuid);
      const newNode = await updateNodeData({
        userAddress: userAddress!,
        workspaceUuid,
        nodeProps: selectedNodeProps,
        nodeUuid,
        oldNode,
        oldNodeData: selectedNodeData,
        updatedNodeData,
      });
      setNodes((nodes) =>
        nodes.map((n) => (n?.data?.uuid === nodeUuid ? newNode : n)),
      );
      console.debug('new nodes:', getNodes());
      setSelectedNodeData(newNode.data);
      toast.success('Save was successful!');
    } catch (error: any) {
      console.error(error);
      toast.error(`Save failed! Error: ${error.message}`, {
        autoClose: false,
        closeOnClick: false,
        draggable: false,
      });
    }
  }

  const [unmetPrerequisites, setUnmetPrerequisites] = useState<any[]>([]);

  const [isAuthModalShown, setIsAuthModalShown] = useState<boolean>(false);
  function toggleIsAuthModalShown() {
    setIsAuthModalShown(!isAuthModalShown);
  }

  async function handleRunPreFlightCheck() {
    const { isReadyToRun, unmetPrerequisites } = await runPreFlightCheck(
      workspace,
    );
    setUnmetPrerequisites(unmetPrerequisites);
    if (!isReadyToRun) {
      console.debug(
        `Workflow failed pre-flight checks. Unmet prerequisites: ${unmetPrerequisites
          .map((p) => `[${p.nodeLabel}: ${p.prerequisite}] ${p.message}`)
          .join(', ')}`,
      );
      // throw new Error(
      //   `Workflow failed pre-flight checks. Unmet prerequisites: ${unmetPrerequisites
      //     .map((p) => `[${p.nodeLabel}: ${p.prerequisite}] ${p.message}`)
      //     .join(', ')}`,
      // );
      if (unmetPrerequisites.some((p) => p.prerequisite === 'OAUTH_1_0A')) {
        setCanDeleteNode(false);
        setIsAuthModalShown(true);
      }
    }
    return { isReadyToRun, unmetPrerequisites };
  }

  return (
    <FlowContext.Provider
      value={{
        isLoading,

        workspace,

        canDeleteNode,
        setCanDeleteNode,

        isSettingsShown,
        setIsSettingsShown,
        toggleIsSettingsShown,

        selectedNodeProps,
        setSelectedNodeProps,

        selectedNodeData,
        setSelectedNodeData,

        updateWorkflowNodeData,

        isNodeDetailsShown,
        setIsNodeDetailsShown,
        toggleIsNodeDetailsShown,

        unmetPrerequisites,
        handleRunPreFlightCheck,

        isAuthModalShown,
        setIsAuthModalShown,
        toggleIsAuthModalShown,
      }}
    >
      {children}
    </FlowContext.Provider>
  );
};
